import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Hosting', path: '/hosting' },
    { name: 'WordPress', path: '/wordpress' },
    { name: 'VibeCoders', path: '/vibecoders' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Use an explicit dark surface on the home route so navigation never relies
  // on the hero being positioned behind this fixed header.
  const isDarkNav = isHomePage && !isScrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-border'
          : isDarkNav
            ? 'bg-foreground border-b border-white/10'
            : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="https://customer-assets.emergentagent.com/job_nextgen-hosting-2/artifacts/0arf8k4y_Complete%20Rectangular%20Logo%20-%20Black.png"
              alt="961Host"
              className={`h-7 w-auto transition-all ${isDarkNav ? 'brightness-0 invert' : ''} group-hover:opacity-70`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? isDarkNav
                      ? 'text-white bg-white/10'
                      : 'text-foreground bg-secondary'
                    : isDarkNav
                      ? 'text-white/70 hover:text-white hover:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              className={isDarkNav ? 'text-white/70 hover:text-white hover:bg-white/10' : ''}
              data-testid="navbar-login-btn"
              asChild
            >
              <a href="https://961host.net/order/index.php/login" target="_blank" rel="noopener noreferrer">
                Login
              </a>
            </Button>
            <Button 
              size="sm"
              className={isDarkNav ? 'bg-white text-foreground hover:bg-white/90' : ''}
              data-testid="navbar-get-started-btn"
              asChild
            >
              <a href="https://961host.net/order/index.php/store/web-hosting" target="_blank" rel="noopener noreferrer">
                Get Started
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isDarkNav 
                ? 'text-white hover:bg-white/10' 
                : 'text-foreground hover:bg-secondary'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`md:hidden ${isDarkNav ? 'bg-foreground' : 'bg-white'} border-t ${isDarkNav ? 'border-white/10' : 'border-border'}`}>
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? isDarkNav
                      ? 'text-white bg-white/10'
                      : 'text-foreground bg-secondary'
                    : isDarkNav
                      ? 'text-white/70 hover:text-white hover:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className={`flex flex-col space-y-2 pt-4 border-t ${isDarkNav ? 'border-white/10' : 'border-border'}`}>
              <Button 
                variant={isDarkNav ? 'outline-dark' : 'outline'} 
                className="w-full" 
                data-testid="mobile-login-btn"
                asChild
              >
                <a href="https://961host.net/order/index.php/login" target="_blank" rel="noopener noreferrer">
                  Login
                </a>
              </Button>
              <Button 
                className={`w-full ${isDarkNav ? 'bg-white text-foreground hover:bg-white/90' : ''}`}
                data-testid="mobile-get-started-btn"
                asChild
              >
                <a href="https://961host.net/order/index.php/store/web-hosting" target="_blank" rel="noopener noreferrer">
                  Get Started
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
